import { useNotification } from '@wheely/ui-kit';
import { IDENTIFIER_TYPES } from '@wheely/web-auth-lib/core';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { updatePartner } from '@/modules/partner/actions/actions';
import { reloadPartner } from '@/modules/partner/actions/async/reloadPartner';
import { getPartner } from '@/modules/partner/store/selectors';
import { AppDispatch } from '@/root-store';
import { StepLayout } from '@/shared/view/StepLayout';

import { IdentifierSection } from './IdentifierSection';

interface IProps {
  onGoBack: () => void;
}

/**
 * Contact editing on a v3 session: the phone changes here, confirmed by a code
 * sent to the new number. The email is not editable — it is the identifier the
 * sign-in leans on, and who may change it is still an open product question.
 */
const ContactInfoEditor = ({ onGoBack }: IProps) => {
  const intl = useIntl();
  const dispatch = useDispatch() as AppDispatch;
  const partner = useSelector(getPartner);
  const { showNotification } = useNotification();

  const handleUpdated = useCallback(
    (_type: unknown, value: string) => {
      // The confirmed value is authoritative for the UI right away; the
      // refetch is best-effort (it only fires for the partner role anyway).
      dispatch(updatePartner({ phone: value }));
      dispatch(reloadPartner());

      showNotification({
        message: intl.formatMessage({
          defaultMessage: 'Phone number updated',
          id: 'profile_phone_updated',
          description: 'Phone change success note',
        }),
        showCloseButton: false,
        dark: true,
      });
    },
    [dispatch, intl, showNotification],
  );

  return (
    <StepLayout
      hideProgress={true}
      title={intl.formatMessage({
        defaultMessage: 'Contact information',
        id: 'N5g41j',
        description: 'Edit Contact information screen: title',
      })}
      onClickBack={onGoBack}
    >
      <IdentifierSection
        identifierType={IDENTIFIER_TYPES.PHONE}
        currentValue={partner.phone}
        onUpdated={handleUpdated}
      />
    </StepLayout>
  );
};

export { ContactInfoEditor };
